import { Fragment, useEffect, useMemo, useRef, useState, type MouseEvent, type ReactNode } from "react";
import type { ProductProfile } from "../../../shared/goals/profile";
import { Icon } from "../../components/ui/Icon";
import { Sheet } from "../../components/ui/Sheet";
import {
  WK_HINT,
  WK_T,
  assumed,
  allRules,
  changesLine,
  dropEdit,
  edited,
  openAll,
  openQs,
  partState,
  setAnswer,
  setEdit,
  splitFirst,
  togglePb as togglePbState,
  toggleRule as toggleRuleState,
  walkFrom,
  walkParts,
  type Part,
  type PartState,
  type WalkState,
} from "./walk";
import { GrowText } from "../../components/ui/GrowText";
import { InPlaceTip } from "../goal/InPlace";
import { PlaybookPart } from "../goal/pbShape";
import { host } from "./text";

const CTA: Record<string, [string, string]> = { works: ["ok", "Works"], broken: ["rl", "Broken"], "not opened": ["", "Not opened"] };

interface Props {
  profile: ProductProfile;
  st: WalkState;
  setSt: (fn: (s: WalkState) => WalkState) => void;
  busy: boolean;
  onConfirm: () => void;
  /** Changing a confirmed profile: its questions were answered when it was confirmed, and the last step saves. */
  edit?: boolean;
}

/** The prototype's profile walk: a rail of parts, and one part at a time to check, fix and answer. */
export function ProfileWalk({ profile, st, setSt, busy, onConfirm, edit = false }: Props) {
  // Why: the owner already confirmed this profile, so nothing in it waits for them as the agent's best reading.
  const p = useMemo(() => (edit ? { ...profile, sections: profile.sections.map((s) => ({ ...s, status: "found" as const })) } : profile), [profile, edit]);
  const parts = useMemo(() => walkParts(p), [p]);
  // Why: a saved profile reopens as the owner left it: every part checked, every answer, review and note in place.
  useEffect(() => {
    if (edit) setSt((s) => (s.base !== undefined ? s : { ...walkFrom(p, parts), at: s.at }));
  }, [edit, p, parts, setSt]);
  const [map, setMap] = useState(false);
  const walkRef = useRef<HTMLDivElement>(null);
  const i = Math.min(st.at, parts.length - 1);
  const x = parts[i];

  const go = (k: number) => {
    setSt((s) => ({ ...s, at: k, noteOpen: null }));
    const w = walkRef.current;
    if (w && w.getBoundingClientRect().top < 0) w.scrollIntoView({ block: "start" });
  };

  const okPart = (k: number) => {
    const part = parts[k];
    if (openQs(part, st).length) return;
    setSt((s) => ({ ...s, ok: { ...s.ok, [part.id]: true } }));
    go(Math.min(k + 1, parts.length - 1));
  };

  const states = parts.map((y) => partState(y, st, p));
  const rail = (onPick: (k: number) => void) => (
    <ol className="wr-list">
      {parts.map((y, k) => (
        <RailItem key={y.id} title={y.title} on={k === i} state={states[k]} onClick={() => onPick(k)} />
      ))}
    </ol>
  );
  const done = parts.filter((y) => st.ok[y.id]).length;

  return (
    <div className="walk" ref={walkRef}>
      <aside className="wrail">
        <div className="wr-h">
          <b>
            {done} of {parts.length}
          </b>{" "}
          checked by you
        </div>
        <div className="wr-bar">
          <i style={{ width: `${(done / parts.length) * 100}%` }} />
        </div>
        {rail(go)}
        <button type="button" className="chip link wr-mob" onClick={() => setMap(true)}>
          Part {i + 1} of {parts.length} · All parts
          <Icon name="chev" />
        </button>
      </aside>
      <section className="card wcard">
        <div className="wk-top">
          <h2 className="wk-h">{x.title}</h2>
          <Marker x={x} state={states[i]} st={st} p={p} />
        </div>
        {WK_HINT[x.id] && <p className="wk-hint">{WK_HINT[x.id]}</p>}
        {x.kind === "site" && <SitePart p={p} st={st} setSt={setSt} x={x} />}
        {x.kind === "sections" && (
          <>
            {x.secs.map((id, k) => (
              <SectionText key={id} id={id} p={p} st={st} setSt={setSt} part={x} sub={x.secs.length > 1} first={k === 0} />
            ))}
            <Questions x={x} p={p} st={st} setSt={setSt} />
            <Note x={x} st={st} setSt={setSt} />
          </>
        )}
        {x.kind === "found" && <Found p={p} st={st} setSt={setSt} />}
        {x.kind === "more" && <More x={x} p={p} parts={parts} st={st} setSt={setSt} go={go} />}
        <Nav parts={parts} i={i} st={st} busy={busy} go={go} okPart={okPart} onConfirm={onConfirm} edit={edit} />
      </section>
      <Sheet open={map} onClose={() => setMap(false)} label="All parts">
        <div className="eyebrow">Your profile</div>
        <h2 style={{ marginTop: 6 }}>All parts</h2>
        <div style={{ marginTop: 12 }}>
          {rail((k) => {
            setMap(false);
            go(k);
          })}
        </div>
      </Sheet>
    </div>
  );
}

function RailItem({ title, on, state, onClick }: { title: string; on: boolean; state: PartState; onClick: () => void }) {
  return (
    <li>
      <button type="button" className={`${on ? "on" : ""} ${state}`} onClick={onClick}>
        <span className="wr-dot">{state === "ok" ? <Icon name="check" /> : state === "edited" ? <Icon name="pen" /> : null}</span>
        <span>{title}</span>
        {state === "you" ? <em>Needs you</em> : state === "edited" ? <em className="e">Your edits</em> : null}
      </button>
    </li>
  );
}

function Marker({ x, state, st, p }: { x: Part; state: PartState; st: WalkState; p: ProductProfile }) {
  if (state === "ok")
    return (
      <span className="chip ok">
        <Icon name="check" />
        Looks right
      </span>
    );
  if (state === "edited" || edited(x, st))
    return (
      <span className="chip rl">
        <Icon name="pen" />
        Your edits
      </span>
    );
  if (assumed(x, p)) return <span className="chip you">Its best reading: please check</span>;
  if (x.secs.length) return <span className="chip">Found on your pages</span>;
  return null;
}

type Edit = { st: WalkState; setSt: Props["setSt"] };

function Foot({ src, undo, onUndo }: { src: ReactNode; undo: boolean; onUndo: () => void }) {
  return (
    <div className="wk-foot">
      <span className="wk-src">{src}</span>
      <button type="button" className={undo ? "wk-undo" : "wk-undo hide"} onClick={onUndo}>
        Undo my edit
      </button>
    </div>
  );
}

/** A section's words, read as paragraphs and lists; any piece is changed where it is. */
function Words({ label, text, original, onChange }: { label: string; text: string; original: string; onChange: (v: string) => void }) {
  return (
    <div className="dw-body wk-words" role="group" aria-label={label}>
      <PlaybookPart id="" text={text} edit={{ original, onChange }} />
    </div>
  );
}

function SectionText({ id, p, st, setSt, part, sub, first }: Edit & { id: string; p: ProductProfile; part: Part; sub: boolean; first: boolean }) {
  const s = p.sections.find((z) => z.id === id);
  if (!s) return null;
  const label = WK_T[id] ?? s.title;
  return (
    <div className="wk-sec">
      {sub && <div className="wk-sub">{label}</div>}
      {first && <InPlaceTip />}
      <Words label={label} text={st.ed[id] ?? s.text} original={s.text} onChange={(v) => setSt((x) => setEdit(x, id, v, s.text, part.id))} />
      <Foot
        undo={st.ed[id] !== undefined}
        onUndo={() => setSt((x) => dropEdit(x, id))}
        src={
          s.sources.length ? (
            <>
              <Icon name="doc" />
              From
              {s.sources.slice(0, 3).map((u, k) => (
                <span className="chip" key={k}>
                  {host(u)}
                </span>
              ))}
            </>
          ) : (
            <>
              <Icon name="bulb" />
              Its reading, not a quote
            </>
          )
        }
      />
    </div>
  );
}

function SitePart({ p, st, setSt, x }: Edit & { p: ProductProfile; x: Part }) {
  const site = (k: "name" | "website", v: string) => setSt((s) => ({ ...s, site: { ...s.site, [k]: v } }));
  return (
    <>
      <p className="wk-lede">Is this the business we should market? Change anything that is off.</p>
      <div className="row2">
        <div>
          <label className="fl" htmlFor="ws-name" style={{ marginTop: 0 }}>
            Name
          </label>
          <input className="field" id="ws-name" value={st.site.name ?? p.business} onChange={(e) => site("name", e.target.value)} />
        </div>
        <div>
          <label className="fl" htmlFor="ws-web" style={{ marginTop: 0 }}>
            Website
          </label>
          <input className="field" id="ws-web" value={st.site.website ?? p.website} onChange={(e) => site("website", e.target.value)} />
        </div>
      </div>
      <div className="wk-sec" style={{ marginTop: 14 }}>
        <div className="wk-sub">{WK_T.summary}</div>
        <InPlaceTip />
        <Words label={WK_T.summary} text={st.ed.summary ?? p.summary} original={p.summary} onChange={(v) => setSt((s) => setEdit(s, "summary", v, p.summary, x.id))} />
        <Foot
          undo={st.ed.summary !== undefined}
          onUndo={() => setSt((s) => dropEdit(s, "summary"))}
          src={
            <>
              <Icon name="bulb" />
              Its summary of everything it read
            </>
          }
        />
      </div>
      <Questions x={x} p={p} st={st} setSt={setSt} />
      <Note x={x} st={st} setSt={setSt} />
    </>
  );
}

function Questions({ x, p, st, setSt }: Edit & { x: Part; p: ProductProfile }) {
  if (!x.qs.length) return null;
  return (
    <div className="wk-q">
      <div className="wk-qh">
        <Icon name="user" />
        Only you know this
      </div>
      {x.qs.map((k) => {
        const q = p.questions[k];
        return (
          <div className="wk-qi" id={`lq-${k}`} key={k}>
            <b>{q.question}</b>
            <p>{q.why}</p>
            {q.options.length ? (
              <Choices k={k} options={q.options} st={st} setSt={setSt} />
            ) : (
              <input
                className="field"
                style={{ marginTop: 8 }}
                placeholder="Your answer"
                aria-label={q.question}
                value={typeof st.ans[k] === "string" ? (st.ans[k] as string) : ""}
                onChange={(e) => {
                  const v = e.target.value;
                  setSt((s) => setAnswer(s, k, v));
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * A question's choices, each with a pencil to change its words, and the owner's own answer. Why: the choices
 * are the agent's best guesses; the owner's words outrank them and reach the agent as written.
 */
function Choices({ k, options, st, setSt }: Edit & { k: number; options: string[] }) {
  const [box, setBox] = useState<{ text: string; own: boolean } | null>(null);
  const a = st.ans[k];
  const own = typeof a === "string" && a.trim() ? a : null;
  const answer = (v: number | string | null) => setSt((s) => setAnswer(s, k, v));
  const save = (v: string) => {
    const t = v.trim();
    const j = options.indexOf(t);
    if (j >= 0) answer(j);
    else if (t) answer(t);
    else if (box?.own) answer(null);
    setBox(null);
  };
  const choice = (text: string, on: boolean, pick: () => void, mine: boolean) => (
    <span className={`btn sm${on ? "" : " ghost"} wk-opt${mine ? " mine" : ""}`}>
      <button type="button" className="wk-pick" aria-pressed={on} onClick={pick}>
        {text}
      </button>
      <button type="button" className="wk-oed" aria-label="Change these words" title="Change these words" onClick={() => setBox({ text, own: mine })}>
        <Icon name="pen" />
      </button>
    </span>
  );
  return (
    <>
      <div className="m-act wk-opts">
        {options.map((o, j) => (
          <Fragment key={j}>{choice(o, a === j, () => answer(j), false)}</Fragment>
        ))}
        {own !== null ? (
          choice(own, true, () => setBox({ text: own, own: true }), true)
        ) : (
          <button type="button" className="btn sm ghost wk-own" onClick={() => setBox({ text: "", own: false })}>
            <Icon name="plus" />
            Your own answer
          </button>
        )}
      </div>
      {box && <AnswerBox key={`${box.own}:${box.text}`} initial={box.text} onDone={save} onCancel={() => setBox(null)} />}
    </>
  );
}

// Why: the box's buttons take the click without taking focus, so leaving the box never saves before Cancel.
const hold = (e: MouseEvent) => e.preventDefault();

function AnswerBox({ initial, onDone, onCancel }: { initial: string; onDone: (v: string) => void; onCancel: () => void }) {
  const [v, setV] = useState(initial);
  const closed = useRef(false);
  const close = (keep: boolean) => {
    if (closed.current) return;
    closed.current = true;
    if (keep) onDone(v);
    else onCancel();
  };
  return (
    <div
      className="ip-ed wk-ans"
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) close(true);
      }}
    >
      <GrowText
        rows={1}
        autoFocus
        aria-label="Your answer"
        placeholder="Your answer, in your words"
        value={v}
        onFocus={(e) => e.currentTarget.setSelectionRange(e.currentTarget.value.length, e.currentTarget.value.length)}
        onChange={(e) => setV(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.preventDefault();
            close(false);
          } else if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            close(true);
          }
        }}
      />
      <span className="ip-edf">
        <span>Enter saves. It goes to the agent as your answer.</span>
        <button type="button" className="btn sm" onMouseDown={hold} onClick={() => close(true)}>
          Done
        </button>
        <button type="button" className="btn sm ghost" onMouseDown={hold} onClick={() => close(false)}>
          Cancel
        </button>
      </span>
    </div>
  );
}

function Note({ x, st, setSt }: Edit & { x: Part }) {
  const v = st.note[x.id] ?? "";
  if (!v && st.noteOpen !== x.id)
    return (
      <div className="wk-note">
        <button type="button" className="chip link" onClick={() => setSt((s) => ({ ...s, noteOpen: x.id }))}>
          <Icon name="plus" />
          Add what the website does not say
        </button>
      </div>
    );
  const close = () =>
    setSt((s) => {
      const note = { ...s.note };
      delete note[x.id];
      return { ...s, note, noteOpen: null };
    });
  return (
    <div className="wk-note">
      <div className="wk-nh">
        <label className="fl" htmlFor={`wn-${x.id}`}>
          What the website does not say
        </label>
        <button type="button" className="wk-undo" onClick={close}>
          {v.trim() ? "Remove note" : "Cancel"}
        </button>
      </div>
      <textarea
        className="field"
        id={`wn-${x.id}`}
        rows={3}
        autoFocus
        placeholder="In your words. This outranks anything it read."
        value={v}
        onChange={(e) => {
          const val = e.target.value;
          setSt((s) => ({ ...s, note: { ...s.note, [x.id]: val } }));
        }}
      />
    </div>
  );
}

/** A fact list folded to one line: its name and a count, opened on a click. */
function Folded({ title, sum, children }: { title: string; sum: ReactNode; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={open ? "wf-ref open" : "wf-ref"}>
      <button type="button" className="wf-refh" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <b>{title}</b>
        <span className="wf-sum">{sum}</span>
        <Icon name="chev" />
      </button>
      {open && <div className="wf-refb">{children}</div>}
    </div>
  );
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
const CTA_ORDER: Record<string, number> = { broken: 0, "not opened": 1, works: 2 };

/**
 * What it noticed while reading. Everything here counts as right unless the owner says otherwise: each finding
 * and rule has one "Not right", which puts the owner's words in its place. The facts it found fold to a line.
 */
function Found({ p, st, setSt }: Edit & { p: ProductProfile }) {
  const [details, setDetails] = useState<Record<number, boolean>>({});
  const [opened, setOpened] = useState<string | null>(null);
  const rules = allRules(p);
  const wrong = (k: number) => st.pb[k] === "wrong";
  const togglePb = (k: number) => {
    setOpened(wrong(k) ? null : `pb${k}`);
    setSt((s) => togglePbState(s, k));
  };
  const toggleRule = (k: number) => {
    setOpened(st.wo[k] !== undefined ? null : `wo${k}`);
    setSt((s) => toggleRuleState(s, k));
  };
  const wrongN = p.problems.filter((_, k) => wrong(k)).length;
  const offN = rules.filter((_, k) => st.wo[k] !== undefined).length;
  const ctas = p.calls_to_action.map((c, k) => ({ c, k })).toSorted((a, b) => (CTA_ORDER[a.c.checked] ?? 1) - (CTA_ORDER[b.c.checked] ?? 1) || a.k - b.k);
  const count = (v: string) => p.calls_to_action.filter((c) => c.checked === v).length;

  return (
    <>
      <p className="wk-lede">What it noticed while reading. Nothing here changes your profile unless you say so.</p>
      {p.problems.length > 0 && (
        <div className="wf-grp">
          <div className="wf-gh">
            <h3>
              Problems on your pages <span className="chip">{p.problems.length}</span>
            </h3>
            {wrongN > 0 && (
              <span className="wf-tally">
                <i />
                {wrongN} not right
              </span>
            )}
          </div>
          {p.problems.map((x, k) => {
            const [head, rest] = splitFirst(x.problem);
            return (
              <div className={wrong(k) ? "wf-row mine" : "wf-row"} key={k}>
                {wrong(k) ? (
                  <div className="wf-mine">
                    <GrowText
                      className="wf-text"
                      rows={1}
                      aria-label="What is right"
                      placeholder="What is right?"
                      autoFocus={opened === `pb${k}`}
                      value={st.pbw[k] ?? ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        setSt((s) => ({ ...s, pbw: { ...s.pbw, [k]: val } }));
                      }}
                    />
                    <p className="wf-was">
                      <b>It said</b>
                      {head}
                    </p>
                  </div>
                ) : (
                  <div className="wf-main">
                    <b className="wf-h">{head}</b>
                    {details[k] && (
                      <>
                        {rest && <p className="wf-b">{rest}</p>}
                        <dl className="wf-meta">
                          <dt>Where</dt>
                          <dd>{x.where}</dd>
                          <dt>Fix</dt>
                          <dd>{x.fix}</dd>
                        </dl>
                      </>
                    )}
                    <button type="button" className={details[k] ? "wf-more open" : "wf-more"} aria-expanded={!!details[k]} onClick={() => setDetails((d) => ({ ...d, [k]: !d[k] }))}>
                      Details
                      <Icon name="chev" />
                    </button>
                  </div>
                )}
                <button type="button" className="wf-nr" aria-pressed={wrong(k)} onClick={() => togglePb(k)}>
                  Not right
                </button>
              </div>
            );
          })}
        </div>
      )}
      {rules.length > 0 && (
        <div className="wf-grp">
          <div className="wf-gh">
            <h3>Rules for your kind of business</h3>
            {offN > 0 && (
              <span className="wf-tally">
                <i />
                {offN} taken out
              </span>
            )}
            <p className="wf-sub">These travel into every goal for this product. One marked not right is taken out.</p>
          </div>
          {rules.map((w, k) => {
            const off = st.wo[k] !== undefined;
            return (
              <div className={off ? "wf-row mine" : "wf-row"} key={k}>
                {off ? (
                  <div className="wf-mine">
                    <GrowText
                      className="wf-text"
                      rows={1}
                      aria-label="What is right"
                      placeholder="What is right?"
                      autoFocus={opened === `wo${k}`}
                      value={st.wo[k]}
                      onChange={(e) => {
                        const val = e.target.value;
                        setSt((s) => ({ ...s, wo: { ...s.wo, [k]: val } }));
                      }}
                    />
                    <p className="wf-was">
                      <b>Taken out</b>
                      {w}
                    </p>
                  </div>
                ) : (
                  <p className="wf-rule">{w}</p>
                )}
                <button type="button" className="wf-nr" aria-pressed={off} onClick={() => toggleRule(k)}>
                  Not right
                </button>
              </div>
            );
          })}
        </div>
      )}
      {(ctas.length > 0 || p.presence.length > 0 || p.market.length > 0) && (
        <div className="wf-refs">
          {ctas.length > 0 && (
            <Folded
              title="Buttons on your site"
              sum={
                <>
                  {count("works") > 0 && (
                    <span>
                      <i className="ok" />
                      {plural(count("works"), "works", "work")}
                    </span>
                  )}
                  {count("broken") > 0 && (
                    <span>
                      <i className="bad" />
                      {count("broken")} broken
                    </span>
                  )}
                  {count("not opened") > 0 && <span>{count("not opened")} not opened</span>}
                </>
              }
            >
              {ctas.map(({ c, k }) => {
                const [cls, label] = CTA[c.checked] ?? CTA["not opened"];
                return (
                  <div className="ctar" key={k}>
                    <div>
                      <b>{c.label}</b>
                      <p>{c.lands_on}</p>
                    </div>
                    <span className={cls ? `chip ${cls}` : "chip"}>{label}</span>
                  </div>
                );
              })}
            </Folded>
          )}
          {p.presence.length > 0 && (
            <Folded title="Where you show up" sum={<span>{plural(p.presence.length, "place", "places")}</span>}>
              {p.presence.map((x, k) => (
                <div className="kv" key={k}>
                  <span>{x.channel}</span>
                  <span>{x.seen}</span>
                </div>
              ))}
            </Folded>
          )}
          {p.market.length > 0 && (
            <Folded title="Your market" sum={<span>{plural(p.market.length, "fact", "facts")}</span>}>
              {p.market.map((m, k) => (
                <div className="fi" key={k}>
                  <span className="n">{String(k + 1).padStart(2, "0")}</span>
                  <div>
                    {m.fact}
                    <br />
                    <span className="chip">{host(m.source)}</span>
                  </div>
                </div>
              ))}
            </Folded>
          )}
        </div>
      )}
    </>
  );
}

function More({ x, p, parts, st, setSt, go }: Edit & { x: Part; p: ProductProfile; parts: Part[]; go: (k: number) => void }) {
  const open = openAll(parts, st).filter((o) => o.x.id !== "more");
  const unchecked = parts.filter((y) => !st.ok[y.id] && y.id !== "more");
  return (
    <>
      <p className="wk-lede">What you add here goes in as your words, and outranks what it read.</p>
      <Questions x={x} p={p} st={st} setSt={setSt} />
      <label className="fl" htmlFor="wk-extra">
        Anything else we should know
      </label>
      <textarea
        className="field"
        id="wk-extra"
        rows={5}
        placeholder="Offers you can make, what you will never say, who your best clients are, what makes you different..."
        value={st.extra}
        onChange={(e) => {
          const v = e.target.value;
          setSt((s) => ({ ...s, extra: v }));
        }}
      />
      <div className="wk-sumbox">
        <b>Your changes</b>
        <p>{changesLine(st)}</p>
        {open.length > 0 && (
          <div className="wk-block">
            {open.map((o) => (
              <button type="button" className="chip link" key={o.x.id} onClick={() => go(o.i)}>
                {o.x.title}: {o.n} question{o.n > 1 ? "s" : ""} open
                <Icon name="right" />
              </button>
            ))}
          </div>
        )}
        {!open.length && unchecked.length > 0 && (
          <p className="wk-soft">
            {unchecked.length} part{unchecked.length > 1 ? "s" : ""} not checked yet. Confirming keeps {unchecked.length > 1 ? "them" : "it"} as found.
          </p>
        )}
      </div>
    </>
  );
}

function Nav({
  parts,
  i,
  st,
  busy,
  go,
  okPart,
  onConfirm,
  edit,
}: {
  parts: Part[];
  i: number;
  st: WalkState;
  busy: boolean;
  go: (k: number) => void;
  okPart: (k: number) => void;
  onConfirm: () => void;
  edit: boolean;
}) {
  const x = parts[i];
  const last = i === parts.length - 1;
  const open = openAll(parts, st).length;
  const here = openQs(x, st).length;
  const hint = last && open ? `${open} question${open > 1 ? "s" : ""} still open` : here && !last ? `${here} question${here > 1 ? "s" : ""} open` : "";
  let primary: ReactNode;
  if (last)
    primary = (
      <button type="button" className="btn" disabled={open > 0 || busy} style={open > 0 ? { opacity: 0.45 } : undefined} onClick={onConfirm}>
        {edit ? "Save changes" : "Confirm the profile"}
        <Icon name="check" />
      </button>
    );
  else if (here)
    primary = (
      <button type="button" className="btn ghost" onClick={() => go(i + 1)}>
        Skip for now
        <Icon name="right" />
      </button>
    );
  else
    primary = (
      <button type="button" className="btn" onClick={() => okPart(i)}>
        {x.kind === "found" ? "Next" : edited(x, st) ? "Save and next" : "Looks right"}
        <Icon name="right" />
      </button>
    );
  return (
    <div className="wk-nav">
      {i > 0 && (
        <button type="button" className="btn ghost" onClick={() => go(i - 1)}>
          <Icon name="left" />
          Back
        </button>
      )}
      <span className="sp">{hint}</span>
      {primary}
    </div>
  );
}
